import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  Box,
  Paper,
  Typography,
  Divider,
  Button,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  IconButton,
  TextField,
  Select,
  MenuItem,
  Autocomplete,
  CircularProgress,
  Tabs,
  Tab,
  FormControlLabel,
  Switch,
} from "@mui/material";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import UploadIcon from "@mui/icons-material/Upload";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import ClearIcon from "@mui/icons-material/Clear";
import { API_URL } from "./config";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

export default function UpdateDegreeSubjectMapping() {
  // ✅ Theme constants (same as your UpdateClasses)
  const THEME_ORANGE = "#F07828";
  const THEME_ORANGE_2 = "#ed7f35ff";
  const BG = "#F2F2F2";
  const BORDER = "rgba(25,118,210,0.20)";
  const BORDER_HOVER = "rgba(25,118,210,0.45)";
  const BORDER_FOCUS = "rgba(240,120,40,0.80)";
  const ROW_ALT = "rgba(25,118,210,0.06)";
  const ROW_HOVER = "rgba(25,118,210,0.10)";

  const pageWrapSx = { p: 2, backgroundColor: BG };
  const rootPaperSx = {
    mx: "auto",
    borderRadius: 2,
    overflow: "hidden",
    border: "1px solid rgba(25,118,210,0.15)",
  };
  const headerSx = {
    px: 2,
    py: 1.5,
    background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 2,
    flexWrap: "wrap",
  };
  const bodySx = { p: 2, backgroundColor: BG };

  const surfaceSx = {
    borderRadius: 2,
    backgroundColor: "white",
    border: `1px solid ${BORDER}`,
  };

  const sectionSx = {
    ...surfaceSx,
    p: 2,
    position: "relative",
    overflow: "hidden",
    "&:before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: 0,
      bottom: 0,
      width: 6,
      background: `linear-gradient(180deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
      opacity: 0.95,
    },
  };

  const inputSx = {
    "& .MuiOutlinedInput-notchedOutline": { borderColor: BORDER },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_HOVER },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_FOCUS },
  };

  const primaryBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    backgroundColor: THEME_ORANGE,
    "&:hover": { backgroundColor: THEME_ORANGE_2 },
  };

  const outlinedBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    borderColor: "rgba(25,118,210,0.35)",
    color: "rgba(25,118,210,0.95)",
    "&:hover": {
      borderColor: "rgba(25,118,210,0.55)",
      backgroundColor: "rgba(25,118,210,0.04)",
    },
  };

  const dangerOutlinedBtnSx = {
    ...outlinedBtnSx,
    borderColor: "rgba(244,67,54,0.35)",
    color: "rgba(244,67,54,0.95)",
    "&:hover": {
      borderColor: "rgba(244,67,54,0.55)",
      backgroundColor: "rgba(244,67,54,0.04)",
    },
  };

  const iconBtnSx = {
    borderRadius: 2,
    color: "rgba(25,118,210,0.95)",
    "&:hover": { backgroundColor: "rgba(240,120,40,0.10)", color: THEME_ORANGE },
  };

  const tableWrapSx = { ...surfaceSx, mt: 2, overflow: "hidden" };

  const theadRowSx = {
    backgroundColor: "rgba(25,118,210,0.95)",
    "& th": {
      color: "#fff",
      fontWeight: 900,
      borderBottom: "1px solid rgba(255,255,255,0.18)",
      whiteSpace: "nowrap",
    },
  };

  const zebraRowSx = (idx) => ({
    backgroundColor: idx % 2 === 0 ? ROW_ALT : "#FFFFFF",
    "&:hover": { backgroundColor: ROW_HOVER },
    "& td": { borderBottom: "1px solid rgba(25,118,210,0.10)" },
  });

  const checkboxSx = {
    color: "rgba(25,118,210,0.75)",
    "&.Mui-checked": { color: THEME_ORANGE },
  };

  // ===== State =====
  const token = localStorage.getItem("auth");
  const [campusCode, setCampusCode] = useState("");
  const [rows, setRows] = useState([]);
  const [loadingLookups, setLoadingLookups] = useState(false);
  const [toast, setToast] = useState({ open: false, type: "success", msg: "" });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const [tab, setTab] = useState(0);
  const [bulkCreating, setBulkCreating] = useState(false);

  const [bulkForm, setBulkForm] = useState({
    SubCampusCode: [],
    DegreeCode: [],
    BranchCode: [],
    Regulation: [],
    Semester: [],
    SubjectCode: [],
    IsActive: 1,
  });

  const [bulkOptions, setBulkOptions] = useState({
    SubCampusCode: [],
    DegreeCode: [],
    BranchCode: [],
    Regulation: [],
    Semester: [],
    Subjects: [],
  });

  const SELECT_ALL_VALUE = "__ALL__";

  const withSelectAll = (list = []) => [
    { label: "Select All", value: SELECT_ALL_VALUE, isSelectAll: true },
    ...list.map((x) =>
      typeof x === "object"
        ? x
        : { label: String(x), value: String(x) }
    ),
  ];

  const getOptionValue = (opt) => opt?.value ?? opt;
  const getOptionLabelText = (opt) => opt?.label ?? String(opt ?? "");

  // ✅ NEW: edit draft (so edits don’t affect filteredRows until Save)
  const [editDraft, setEditDraft] = useState(null);

  const fileInputRef = useRef(null);

  // edit/new row
  const [editRowId, setEditRowId] = useState(null);
  const [newRow, setNewRow] = useState({
    SubCampusCode: "",
    DegreeCode: "",
    BranchCode: "",
    Regulation: "",
    Semester: "",
    SubjectCode: "",
    IsActive: 1,
  });

  // dropdown options
  const [options, setOptions] = useState({
    SubCampusCode: [],
    DegreeCode: [],
    BranchCode: [],
    Regulation: [],
    Semester: [],
    Subjects: [], // [{SubjectCode, SubjectName, SubjectNameCode}]
  });

  const [addRowOptions, setAddRowOptions] = useState({
    SubCampusCode: [],
    DegreeCode: [],
    BranchCode: [],
    Regulation: [],
    Semester: [],
    Subjects: [],
  });
  // filters gate
  const [filtersApplied, setFiltersApplied] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);

  const [filters, setFilters] = useState({
    SubCampusCode: [],
    DegreeCode: [],
    BranchCode: [],
    Regulation: [],
    Semester: [],
    SubjectCode: [],
    IsActive: [], // ["1"] or ["0"]
  });

  // ---- helpers ----
  const authHeader = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  useEffect(() => {
    if (!token) return;
    try {
      const decoded = JSON.parse(atob(token.split(".")[1]));
      setCampusCode(decoded.campusCode);
    } catch {
      console.error("Failed to decode token");
    }
  }, [token]);

  const hasAnyFilterSelected = useMemo(() => {
    return Object.values(filters).some((arr) => Array.isArray(arr) && arr.length > 0);
  }, [filters]);

  const norm = (v) => String(v ?? "").trim().toLowerCase();
  const inFilter = (selected = [], value) => !selected?.length || selected.some((s) => norm(s) === norm(value));

  // ---- fetch lookups ----
  useEffect(() => {
    if (!campusCode) return;

    axios
      .get(`${API_URL}/api/lookups/degreeSubjectMapAddRow`, {
        ...authHeader,
        params: { CampusCode: campusCode },
      })
      .then((res) => {
        setAddRowOptions((p) => ({
          ...p,
          SubCampusCode: res.data?.SubCampusCode || [],
        }));
      })
      .catch((e) => {
        console.error(e);
        setToast({
          open: true,
          type: "error",
          msg: "Failed to load add-row subcampus dropdown.",
        });
      });
  }, [campusCode, authHeader]);

  useEffect(() => {
    if (!campusCode) return;
    let cancelled = false;

    (async () => {
      try {
        setLoadingLookups(true);
        const { data } = await axios.get(`${API_URL}/api/lookups/degreeSubjectMap`, {
          ...authHeader,
          params: { CampusCode: campusCode },
        });
        if (cancelled) return;

        setOptions({
          SubCampusCode: data?.SubCampusCode || [],
          DegreeCode: data?.DegreeCode || [],
          BranchCode: data?.BranchCode || [],
          Regulation: data?.Regulation || [],
          Semester: (data?.Semester || []).map(String),
          Subjects: data?.Subjects || [],
        });
      } catch (e) {
        console.error(e);
        setToast({ open: true, type: "error", msg: "Failed to load dropdowns." });
      } finally {
        if (!cancelled) setLoadingLookups(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [campusCode, authHeader]);

  useEffect(() => {
    if (!campusCode) return;

    axios
      .get(`${API_URL}/api/lookups/degreeSubjectMapAddRow`, {
        ...authHeader,
        params: { CampusCode: campusCode },
      })
      .then((res) => {
        setBulkOptions((p) => ({
          ...p,
          SubCampusCode: res.data?.SubCampusCode || [],
        }));
      })
      .catch(console.error);
  }, [campusCode, authHeader]);

  useEffect(() => {
    if (!campusCode) return;

    const loadBulkOptions = async () => {
      try {
        if (!bulkForm.SubCampusCode.length) {
          setBulkOptions((p) => ({
            ...p,
            DegreeCode: [],
            BranchCode: [],
            Regulation: [],
            Semester: [],
            Subjects: [],
          }));
          return;
        }

        const degRes = await axios.get(`${API_URL}/api/lookups/degreeSubjectMapAddRow`, {
          ...authHeader,
          params: {
            CampusCode: campusCode,
            SubCampusCode: bulkForm.SubCampusCode.join(","),
          },
        });

        if (!bulkForm.DegreeCode.length) {
          setBulkOptions((p) => ({
            ...p,
            DegreeCode: degRes.data?.DegreeCode || [],
            BranchCode: [],
            Regulation: [],
            Semester: [],
            Subjects: [],
          }));
          return;
        }

        const brRes = await axios.get(`${API_URL}/api/lookups/degreeSubjectMapAddRow`, {
          ...authHeader,
          params: {
            CampusCode: campusCode,
            SubCampusCode: bulkForm.SubCampusCode.join(","),
            DegreeCode: bulkForm.DegreeCode.join(","),
          },
        });

        const subjRes = await axios.get(`${API_URL}/api/lookups/degreeSubjectMapAddRowSubjects`, {
          ...authHeader,
          params: {
            CampusCode: campusCode,
            Regulation: bulkForm.Regulation.join(","),
            Semester: bulkForm.Semester.join(","),
          },
        });

        setBulkOptions((p) => ({
          ...p,
          DegreeCode: degRes.data?.DegreeCode || [],
          BranchCode: brRes.data?.BranchCode || [],
          Regulation: subjRes.data?.Regulation || [],
          Semester: subjRes.data?.Semester || [],
          Subjects: subjRes.data?.Subjects || [],
        }));
      } catch (e) {
        console.error(e);
        setToast({
          open: true,
          type: "error",
          msg: "Failed to load bulk mapping dropdowns.",
        });
      }
    };

    loadBulkOptions();
  }, [
    campusCode,
    authHeader,
    bulkForm.SubCampusCode,
    bulkForm.DegreeCode,
    bulkForm.Regulation,
    bulkForm.Semester,
  ]);  
  // ---- fetch mappings ----
  const fetchData = async () => {
    const res = await axios.get(`${API_URL}/api/DegreeSubjectMap`, {
      ...authHeader,
      params: { CampusCode: campusCode },
    });
    setRows(res.data || []);
  };

  useEffect(() => {
    if (!campusCode) return;
    fetchData().catch(() => setError("Failed to load mappings"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campusCode]);

  const filteredRows = useMemo(() => {
    if (!filtersApplied || !hasAnyFilterSelected) return [];

    return (rows || []).filter((r) => {
      const okSub = inFilter(filters.SubCampusCode, r.SubCampusCode);
      const okDeg = inFilter(filters.DegreeCode, r.DegreeCode);
      const okBr = inFilter(filters.BranchCode, r.BranchCode);
      const okReg = inFilter(filters.Regulation, r.Regulation);
      const okSem = inFilter(filters.Semester, String(r.Semester));
      const okSubj = inFilter(filters.SubjectCode, r.SubjectCode);
      const okActive =
        !filters.IsActive.length || filters.IsActive.some((x) => String(x) === (r.IsActive ? "1" : "0"));

      return okSub && okDeg && okBr && okReg && okSem && okSubj && okActive;
    });
  }, [rows, filters, filtersApplied, hasAnyFilterSelected]);

  // selection
  const allSelected = filteredRows.length > 0 && filteredRows.every((r) => selectedRows.includes(r.MapID));
  const isRowSelected = (id) => selectedRows.includes(id);

  const handleSelectAll = (e) => {
    if (e.target.checked) setSelectedRows(filteredRows.map((r) => r.MapID));
    else setSelectedRows([]);
  };

  const handleSelectRow = (id) => (e) => {
    if (e.target.checked) setSelectedRows((prev) => [...prev, id]);
    else setSelectedRows((prev) => prev.filter((x) => x !== id));
  };

  // filter/clear
  const handleApplyFilters = () => {
    if (!hasAnyFilterSelected) {
      setToast({ open: true, type: "warning", msg: "Select at least one filter, then click Filter." });
      setFiltersApplied(false);
      setSelectedRows([]);
      return;
    }
    setFiltersApplied(true);
    setSelectedRows([]);
  };

  const handleClearFilters = () => {
    setFilters({
      SubCampusCode: [],
      DegreeCode: [],
      BranchCode: [],
      Regulation: [],
      Semester: [],
      SubjectCode: [],
      IsActive: [],
    });
    setFiltersApplied(false);
    setSelectedRows([]);

    // ✅ also exit edit mode cleanly
    setEditRowId(null);
    setEditDraft(null);
  };

  // ✅ FIX 1: Checkbox uses checked; FIX 2: edit mode uses editDraft (no rows mutation)
  const handleInputChange = (e, id = null) => {
    const { name, value, checked, type } = e.target;

    let v = value;
    if (type === "checkbox" || name === "IsActive") v = checked ? 1 : 0;

    if (id == null) {
      setNewRow((r) => ({ ...r, [name]: v }));
    } else {
      setEditDraft((d) => ({ ...(d || {}), [name]: v }));
    }
  };

  // create
  // ✅ Add-row specific setters (no reliance on e.target.name)
  const handleNewRowChange = async (field, value) => {
    setNewRow((r) => {
      const next = { ...r, [field]: value };

      if (field === "SubCampusCode") {
        next.DegreeCode = "";
        next.BranchCode = "";
        next.Regulation = "";
        next.Semester = "";
        next.SubjectCode = "";
      }

      if (field === "DegreeCode") {
        next.BranchCode = "";
        next.Regulation = "";
        next.Semester = "";
        next.SubjectCode = "";
      }

      if (field === "BranchCode") {
        next.Regulation = "";
        next.Semester = "";
        next.SubjectCode = "";
      }

      if (field === "Regulation") {
        next.Semester = "";
        next.SubjectCode = "";
      }

      if (field === "Semester") {
        next.SubjectCode = "";
      }

      return next;
    });

    const nextRow = { ...newRow, [field]: value };

    try {
      if (field === "SubCampusCode") {
        const { data } = await axios.get(`${API_URL}/api/lookups/degreeSubjectMapAddRow`, {
          ...authHeader,
          params: {
            CampusCode: campusCode,
            SubCampusCode: value,
          },
        });

        setAddRowOptions((p) => ({
          ...p,
          DegreeCode: data?.DegreeCode || [],
          BranchCode: [],
          Regulation: [],
          Semester: [],
          Subjects: [],
        }));
      }

      if (field === "DegreeCode") {
        const { data } = await axios.get(`${API_URL}/api/lookups/degreeSubjectMapAddRow`, {
          ...authHeader,
          params: {
            CampusCode: campusCode,
            SubCampusCode: nextRow.SubCampusCode,
            DegreeCode: value,
          },
        });

        setAddRowOptions((p) => ({
          ...p,
          BranchCode: data?.BranchCode || [],
          Regulation: [],
          Semester: [],
          Subjects: [],
        }));
      }

      if (field === "BranchCode") {
        const { data } = await axios.get(`${API_URL}/api/lookups/degreeSubjectMapAddRowSubjects`, {
          ...authHeader,
          params: {
            CampusCode: campusCode,
          },
        });

        setAddRowOptions((p) => ({
          ...p,
          Regulation: data?.Regulation || [],
          Semester: [],
          Subjects: [],
        }));
      }

      if (field === "Regulation") {
        const { data } = await axios.get(`${API_URL}/api/lookups/degreeSubjectMapAddRowSubjects`, {
          ...authHeader,
          params: {
            CampusCode: campusCode,
            Regulation: value,
          },
        });

        setAddRowOptions((p) => ({
          ...p,
          Semester: data?.Semester || [],
          Subjects: [],
        }));
      }

      if (field === "Semester") {
        const { data } = await axios.get(`${API_URL}/api/lookups/degreeSubjectMapAddRowSubjects`, {
          ...authHeader,
          params: {
            CampusCode: campusCode,
            Regulation: nextRow.Regulation,
            Semester: value,
          },
        });

        setAddRowOptions((p) => ({
          ...p,
          Subjects: data?.Subjects || [],
        }));
      }
    } catch (e) {
      console.error(e);
      setToast({
        open: true,
        type: "error",
        msg: "Failed to load dependent add-row dropdown.",
      });
    }
  };
  
  const handleNewRowActiveToggle = (e) => {
    setNewRow((r) => ({ ...r, IsActive: e.target.checked ? 1 : 0 }));
  };

  const handleAddRow = async () => {
    const prev = rows;
    try {
      await axios.post(`${API_URL}/api/DegreeSubjectMap`, { ...newRow }, authHeader);
      setSuccess(true);
      setError("");
      await fetchData();
      setNewRow({
        SubCampusCode: "",
        DegreeCode: "",
        BranchCode: "",
        Regulation: "",
        Semester: "",
        SubjectCode: "",
        IsActive: 1,
      });
    } catch (e) {
      setRows(prev);
      setError(e.response?.data?.error || "Failed to add mapping");
    }
  };

  // ✅ update uses editDraft
  const handleSaveRow = async (id) => {
    const prev = rows;

    const updated =
      editDraft && editDraft.MapID === id ? editDraft : rows.find((r) => r.MapID === id);

    try {
      await axios.put(`${API_URL}/api/DegreeSubjectMap/${id}`, updated, authHeader);
      setSuccess(true);
      setError("");
      await fetchData();

      setEditRowId(null);
      setEditDraft(null);
    } catch (e) {
      setRows(prev);
      setError(e.response?.data?.error || "Failed to save changes");
    }
  };

  // delete
  const handleDeleteRow = async (id) => {
    const prev = rows;
    try {
      await axios.delete(`${API_URL}/api/DegreeSubjectMap/${id}`, authHeader);
      setSuccess(true);
      setError("");
      await fetchData();
    } catch (e) {
      setRows(prev);
      setError(e.response?.data?.error || "Failed to delete mapping");
    }
  };

  // bulk active/inactive (kept same)
  const handleBulkActiveUpdate = async (makeActive) => {
    if (!selectedRows.length) return;
    try {
      const promises = selectedRows.map((id) => {
        const row = rows.find((r) => r.MapID === id);
        if (!row) return Promise.resolve();
        return axios.put(
          `${API_URL}/api/DegreeSubjectMap/${id}`,
          { ...row, IsActive: makeActive ? 1 : 0 },
          authHeader
        );
      });

      await Promise.all(promises);
      setToast({ open: true, type: "success", msg: `Updated ${selectedRows.length} row(s)` });
      setSelectedRows([]);
      await fetchData();
    } catch (e) {
      console.error(e);
      setToast({ open: true, type: "error", msg: "Failed bulk update." });
    }
  };

  // excel sample download
  const downloadSampleExcel = () => {
    const headers = ["SubCampusCode", "DegreeCode", "BranchCode", "Regulation", "Semester", "SubjectCode", "IsActive"];
    const sampleRows = [
      {
        SubCampusCode: "SC01",
        DegreeCode: "BTECH",
        BranchCode: "CSE",
        Regulation: "R23",
        Semester: "1",
        SubjectCode: "CS101",
        IsActive: 1,
      },
    ];

    const ws = XLSX.utils.json_to_sheet(sampleRows, { header: headers });
    ws["!cols"] = headers.map((h) => ({ wch: Math.max(16, h.length + 2) }));

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "DegreeSubjectMap");

    const out = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([out], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, `DegreeSubjectMap_Sample_${campusCode || "CAMPUS01"}.xlsx`);
  };

  // bulk upload
  const handleBulkClick = () => fileInputRef.current?.click();

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const form = new FormData();
    form.append("excelFile", file);

    try {
      await axios.post(`${API_URL}/api/UploadDegreeSubjectMap`, form, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });
      await fetchData();
      setToast({ open: true, type: "success", msg: "Bulk upload successful." });
    } catch (err) {
      setToast({ open: true, type: "error", msg: err.response?.data?.error || "Bulk upload failed." });
    } finally {
      e.target.value = "";
    }
  };

  const handleBulkMultiSelectAll = (field, list) => (_, value) => {
    const hasAll = value.some((x) => getOptionValue(x) === SELECT_ALL_VALUE);

    setBulkForm((prev) => {
      const next = {
        ...prev,
        [field]: hasAll
          ? list.map((x) => getOptionValue(x))
          : value.map(getOptionValue),
      };

      if (field === "SubCampusCode") {
        next.DegreeCode = [];
        next.BranchCode = [];
        next.Regulation = [];
        next.Semester = [];
        next.SubjectCode = [];
      }

      if (field === "DegreeCode") {
        next.BranchCode = [];
        next.Regulation = [];
        next.Semester = [];
        next.SubjectCode = [];
      }

      if (field === "BranchCode") {
        next.Regulation = [];
        next.Semester = [];
        next.SubjectCode = [];
      }

      if (field === "Regulation") {
        next.Semester = [];
        next.SubjectCode = [];
      }

      if (field === "Semester") {
        next.SubjectCode = [];
      }

      return next;
    });
  };  

  const handleBulkCreateMappings = async () => {
    const f = bulkForm;

    if (
      !f.SubCampusCode.length ||
      !f.DegreeCode.length ||
      !f.BranchCode.length ||
      !f.Regulation.length ||
      !f.Semester.length ||
      !f.SubjectCode.length
    ) {
      setToast({
        open: true,
        type: "warning",
        msg: "Select SubCampus, Degree, Branch, Regulation, Semester, and Subjects.",
      });
      return;
    }

    try {
      setBulkCreating(true);

      const { data } = await axios.post(
        `${API_URL}/api/DegreeSubjectMap/bulk-create`,
        f,
        authHeader
      );

      setToast({
        open: true,
        type: "success",
        msg: `Created ${data.inserted} mapping(s). Skipped ${data.skipped} duplicate(s).`,
      });

      await fetchData();
      setFilters((p) => ({
        ...p,
        SubCampusCode: f.SubCampusCode,
        DegreeCode: f.DegreeCode,
        BranchCode: f.BranchCode,
      }));
      setFiltersApplied(true);
      setTab(0);
    } catch (e) {
      setToast({
        open: true,
        type: "error",
        msg: e.response?.data?.error || "Bulk create mappings failed.",
      });
    } finally {
      setBulkCreating(false);
    }
  };
  // subject label helper
{/*
  const subjectLabel = (code) => {
    const s = options.Subjects.find((x) => String(x.SubjectCode) === String(code));
    if (!s) return String(code || "");
    return `${s.SubjectCode} - ${s.SubjectName}`;
  };
*/}
  const subjectLabel = (code) => {
    const allSubjects = [
      ...(options.Subjects || []),
      ...(addRowOptions.Subjects || []),
    ];

    const s = allSubjects.find((x) =>
      String(typeof x === "object" ? x.SubjectCode : x) === String(code)
    );

    if (!s) return String(code || "");

    if (typeof s !== "object") return String(s);

    return `${s.SubjectCode} - ${s.SubjectName || s.SubjectNameCode || ""}`;
  };

  return (
    <Box sx={pageWrapSx}>
      <Paper elevation={5} sx={rootPaperSx}>
        {/* Header */}
        <Box sx={headerSx}>
          <Box>
            <Typography sx={{ fontSize: "1.1rem", fontWeight: 900, lineHeight: 1.2 }}>
              Update Degree–Subject Mapping
            </Typography>
            <Typography sx={{ opacity: 0.9, fontSize: "0.85rem" }}>
              Map subjects to Degree/Branch/Regulation/Semester
            </Typography>
          </Box>

          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button
              variant="contained"
              sx={primaryBtnSx}
              disabled={selectedRows.length === 0}
              onClick={() => handleBulkActiveUpdate(true)}
            >
              Set Bulk Active
            </Button>

            <Button
              variant="outlined"
              sx={outlinedBtnSx}
              disabled={selectedRows.length === 0}
              onClick={() => handleBulkActiveUpdate(false)}
            >
              Set Bulk Inactive
            </Button>

            <Divider flexItem orientation="vertical" sx={{ borderColor: "rgba(255,255,255,0.35)" }} />

            <Button variant="outlined" sx={outlinedBtnSx} onClick={downloadSampleExcel}>
              Download Sample
            </Button>

            <Button variant="contained" sx={primaryBtnSx} startIcon={<UploadIcon />} onClick={handleBulkClick}>
              Bulk Upload
            </Button>

            <input
              type="file"
              accept=".xlsx,.xls"
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={handleFile}
            />
          </Box>
        </Box>
<Paper elevation={0} sx={{ ...surfaceSx, m: 2, overflow: "hidden" }}>
  <Tabs
    value={tab}
    onChange={(_, v) => setTab(v)}
    variant="scrollable"
    scrollButtons="auto"
    sx={{
      px: 1,
      borderBottom: `1px solid ${BORDER}`,
      backgroundColor: "#fff",
      "& .MuiTab-root": {
        textTransform: "none",
        fontWeight: 900,
        minHeight: 52,
      },
      "& .Mui-selected": {
        color: THEME_ORANGE,
      },
      "& .MuiTabs-indicator": {
        backgroundColor: THEME_ORANGE,
        height: 3,
        borderRadius: 2,
      },
    }}
  >
    <Tab label="Manage Mapping" />
    <Tab label="Bulk Create Mapping" icon={<AddCircleIcon />} iconPosition="start" />
  </Tabs>

  <Box sx={{ p: 2, backgroundColor: BG }}>
    {tab === 0 && (
      <>
   {/* <Box sx={bodySx}> */}
          {!!error && (
            <Alert
              severity="error"
              onClose={() => setError("")}
              sx={{
                mt: 2,
                borderRadius: 2,
                border: "1px solid rgba(244,67,54,0.25)",
                backgroundColor: "rgba(244,67,54,0.04)",
              }}
            >
              {error}
            </Alert>
          )}

          {!!toast?.open && (
            <Snackbar open autoHideDuration={3500} onClose={() => setToast((t) => ({ ...t, open: false }))}>
              <Alert severity={toast.type || "success"} sx={{ borderRadius: 2 }}>
                {toast.msg}
              </Alert>
            </Snackbar>
          )}

          {!!success && (
            <Snackbar open autoHideDuration={3000} onClose={() => setSuccess(false)}>
              <Alert severity="success" sx={{ borderRadius: 2 }}>
                Operation successful!
              </Alert>
            </Snackbar>
          )}

          {/* Filters */}
          <Paper variant="outlined" sx={sectionSx}>
            <Box sx={{ pl: 1 }}>
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                }}
              >
                <Typography sx={{ fontWeight: 900, color: THEME_ORANGE }}>Filters</Typography>

                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  <Button
                    variant="contained"
                    sx={primaryBtnSx}
                    startIcon={<FilterAltIcon />}
                    onClick={handleApplyFilters}
                    disabled={!hasAnyFilterSelected}
                    title={!hasAnyFilterSelected ? "Select at least one filter" : "Apply filters"}
                  >
                    Filter
                  </Button>
                  <Button variant="outlined" sx={dangerOutlinedBtnSx} startIcon={<ClearIcon />} onClick={handleClearFilters}>
                    Clear
                  </Button>
                </Box>
              </Box>

              <Divider sx={{ my: 2, borderColor: "rgba(25,118,210,0.12)" }} />

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(7, 1fr)" },
                  gap: 1,
                }}
              >
                <Autocomplete
                  multiple
                  options={options.SubCampusCode}
                  value={filters.SubCampusCode}
                  onChange={(_, value) => setFilters((p) => ({ ...p, SubCampusCode: value }))}
                  disableCloseOnSelect
                  filterSelectedOptions
                  openOnFocus
                  loading={loadingLookups}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="SubCampusCode(s)"
                      size="small"
                      sx={inputSx}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingLookups ? <CircularProgress size={16} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />

                <Autocomplete
                  multiple
                  options={options.DegreeCode}
                  value={filters.DegreeCode}
                  onChange={(_, value) => setFilters((p) => ({ ...p, DegreeCode: value }))}
                  disableCloseOnSelect
                  filterSelectedOptions
                  openOnFocus
                  loading={loadingLookups}
                  renderInput={(params) => <TextField {...params} label="DegreeCode(s)" size="small" sx={inputSx} />}
                />

                <Autocomplete
                  multiple
                  options={options.BranchCode}
                  value={filters.BranchCode}
                  onChange={(_, value) => setFilters((p) => ({ ...p, BranchCode: value }))}
                  disableCloseOnSelect
                  filterSelectedOptions
                  openOnFocus
                  loading={loadingLookups}
                  renderInput={(params) => <TextField {...params} label="BranchCode(s)" size="small" sx={inputSx} />}
                />

                <Autocomplete
                  multiple
                  options={options.Regulation}
                  value={filters.Regulation}
                  onChange={(_, value) => setFilters((p) => ({ ...p, Regulation: value }))}
                  disableCloseOnSelect
                  filterSelectedOptions
                  openOnFocus
                  loading={loadingLookups}
                  renderInput={(params) => <TextField {...params} label="Regulation(s)" size="small" sx={inputSx} />}
                />

                <Autocomplete
                  multiple
                  options={options.Semester}
                  value={filters.Semester}
                  onChange={(_, value) => setFilters((p) => ({ ...p, Semester: value.map(String) }))}
                  disableCloseOnSelect
                  filterSelectedOptions
                  openOnFocus
                  loading={loadingLookups}
                  renderInput={(params) => <TextField {...params} label="Semester(s)" size="small" sx={inputSx} />}
                />

                <Autocomplete
                  multiple
                  options={options.Subjects.map((s) => s.SubjectCode)}
                  value={filters.SubjectCode}
                  onChange={(_, value) => setFilters((p) => ({ ...p, SubjectCode: value }))}
                  disableCloseOnSelect
                  filterSelectedOptions
                  openOnFocus
                  loading={loadingLookups}
                  renderInput={(params) => <TextField {...params} label="SubjectCode(s)" size="small" sx={inputSx} />}
                />

                <Autocomplete
                  multiple
                  options={["1", "0"]}
                  value={filters.IsActive}
                  onChange={(_, value) => setFilters((p) => ({ ...p, IsActive: value }))}
                  disableCloseOnSelect
                  filterSelectedOptions
                  openOnFocus
                  loading={loadingLookups}
                  renderInput={(params) => <TextField {...params} label="IsActive (1/0)" size="small" sx={inputSx} />}
                />
              </Box>

              {!filtersApplied && (
                <Alert
                  severity="info"
                  sx={{
                    mt: 2,
                    borderRadius: 2,
                    border: "1px solid rgba(25,118,210,0.20)",
                    backgroundColor: "rgba(25,118,210,0.04)",
                  }}
                >
                  Select at least one filter and click <b>Filter</b> to show results.
                </Alert>
              )}
            </Box>
          </Paper>

          {/* Table */}
      {/* OUTER – keeps rounded corners & spacing */}
      <Paper elevation={0} sx={tableWrapSx}>

        {/* INNER – ONLY for horizontal scroll */}
        <Box
          sx={{
            overflowX: "auto",
            overflowY: "hidden",
          }}
        >
          <TableContainer component="div">
            <Table size="small" sx={{ width: "100%" }}>
              <colgroup>
                <col style={{ width: 50 }} />
                <col style={{ width: 40 }} />
                <col style={{ width: 100 }} />
                <col style={{ width: 100 }} />
                <col style={{ width: 100 }} />
                <col style={{ width: 90 }} />
                <col style={{ width: 90 }} />
                <col style={{ width: 140 }} />
                <col style={{ width: 70 }} />
                <col style={{ width: 120 }} />
              </colgroup>

              <TableHead>
                <TableRow sx={theadRowSx}>
                  <TableCell>#</TableCell>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={allSelected}
                      indeterminate={selectedRows.length > 0 && !allSelected}
                      onChange={handleSelectAll}
                      disabled={filteredRows.length === 0}
                      sx={{ ...checkboxSx, color: "rgba(255,255,255,0.95)", "&.Mui-checked": { color: "#fff" } }}
                    />
                  </TableCell>
                  {["SubCampus", "Degree", "Branch", "Regulation", "Semester", "Subject", "Active", "Actions"].map((h) => (
                    <TableCell key={h}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {filteredRows.map((row, idx) =>
                  editRowId === row.MapID ? (
                    <TableRow key={row.MapID} sx={zebraRowSx(idx)}>
                      {(() => {
                        const d = editDraft || row;
                        return (
                          <>
                            <TableCell>{idx + 1}</TableCell>
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={isRowSelected(row.MapID)}
                                onChange={handleSelectRow(row.MapID)}
                                sx={checkboxSx}
                              />
                            </TableCell>

                            {["SubCampusCode", "DegreeCode", "BranchCode", "Regulation"].map((f) => (
                              <TableCell key={f}>
                                <Select
                                  name={f}
                                  value={d[f] || ""}
                                  onChange={(e) => handleInputChange(e, row.MapID)}
                                  fullWidth
                                  size="small"
                                  sx={inputSx}
                                  displayEmpty
                                >
                                  {(f === "SubCampusCode"
                                    ? options.SubCampusCode
                                    : f === "DegreeCode"
                                    ? options.DegreeCode
                                    : f === "BranchCode"
                                    ? options.BranchCode
                                    : options.Regulation
                                  ).map((opt) => (
                                    <MenuItem key={opt} value={opt}>
                                      {opt}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </TableCell>
                            ))}

                            <TableCell>
                              <Select
                                name="Semester"
                                value={String(d.Semester || "")}
                                onChange={(e) => handleInputChange(e, row.MapID)}
                                fullWidth
                                size="small"
                                sx={inputSx}
                                displayEmpty
                              >
                                {options.Semester.map((s) => (
                                  <MenuItem key={s} value={s}>
                                    {s}
                                  </MenuItem>
                                ))}
                              </Select>
                            </TableCell>

                            <TableCell>
                              <Select
                                name="SubjectCode"
                                value={d.SubjectCode || ""}
                                onChange={(e) => handleInputChange(e, row.MapID)}
                                fullWidth
                                size="small"
                                sx={inputSx}
                                displayEmpty
                                renderValue={(val) => subjectLabel(val)}
                              >
                                {options.Subjects.map((s) => (
                                  <MenuItem key={s.SubjectCode} value={s.SubjectCode}>
                                    {s.SubjectCode} - {s.SubjectName}
                                  </MenuItem>
                                ))}
                              </Select>
                            </TableCell>

                            <TableCell>
                              <Checkbox
                                name="IsActive"
                                checked={!!d.IsActive}
                                onChange={(e) => handleInputChange(e, row.MapID)}
                                sx={checkboxSx}
                              />
                            </TableCell>

                            <TableCell>
                              <IconButton sx={iconBtnSx} onClick={() => handleSaveRow(row.MapID)} title="Save">
                                <SaveIcon />
                              </IconButton>
                              <IconButton
                                sx={iconBtnSx}
                                onClick={() => {
                                  setEditRowId(null);
                                  setEditDraft(null);
                                }}
                                title="Cancel"
                              >
                                <CancelIcon />
                              </IconButton>
                            </TableCell>
                          </>
                        );
                      })()}
                    </TableRow>
                  ) : (
                    <TableRow key={row.MapID} sx={zebraRowSx(idx)}>
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell padding="checkbox">
                        <Checkbox checked={isRowSelected(row.MapID)} onChange={handleSelectRow(row.MapID)} sx={checkboxSx} />
                      </TableCell>

                      <TableCell>{row.SubCampusCode}</TableCell>
                      <TableCell>{row.DegreeCode}</TableCell>
                      <TableCell>{row.BranchCode}</TableCell>
                      <TableCell>{row.Regulation}</TableCell>
                      <TableCell>{row.Semester}</TableCell>
                      <TableCell>{subjectLabel(row.SubjectCode)}</TableCell>

                      <TableCell>
                        <Checkbox checked={!!row.IsActive} readOnly sx={checkboxSx} />
                      </TableCell>

                      <TableCell>
                        <IconButton
                          sx={iconBtnSx}
                          onClick={() => {
                            setEditRowId(row.MapID);
                            setEditDraft({
                              ...row,
                              IsActive: row.IsActive ? 1 : 0,
                              Semester: String(row.Semester ?? ""),
                            });
                          }}
                          title="Edit"
                        >
                          <EditIcon />
                        </IconButton>

                        <IconButton sx={iconBtnSx} onClick={() => handleDeleteRow(row.MapID)} title="Delete">
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  )
                )}

                {/* New row */}
                <TableRow sx={zebraRowSx(filteredRows.length)}>
                  <TableCell>{rows.length + 1}</TableCell>

                  <TableCell padding="checkbox">
                    <Checkbox disabled sx={checkboxSx} />
                  </TableCell>

                  {[
                    ["SubCampusCode", addRowOptions.SubCampusCode],
                    ["DegreeCode", addRowOptions.DegreeCode],
                    ["BranchCode", addRowOptions.BranchCode],
                    ["Regulation", addRowOptions.Regulation],
                  ].map(([field, list]) => (
                    <TableCell key={field}>
                      <Select
                        value={newRow[field] ?? ""}
                        onChange={(e) => handleNewRowChange(field, e.target.value)}
                        fullWidth
                        size="small"
                        sx={inputSx}
                        displayEmpty
                      >
                        <MenuItem value="">
                          <em>Select</em>
                        </MenuItem>
                  
                        {(list || []).map((opt) => (
                          <MenuItem key={opt} value={opt}>
                            {opt}
                          </MenuItem>
                        ))}
                      </Select>
                    </TableCell>
                  ))}

                  <TableCell>
                    <Select
                      value={String(newRow.Semester ?? "")}
                      onChange={(e) => handleNewRowChange("Semester", String(e.target.value))}
                      fullWidth
                      size="small"
                      sx={inputSx}
                      displayEmpty
                    >
                      <MenuItem value="">
                        <em>Select</em>
                      </MenuItem>
                
                      {(addRowOptions.Semester || []).map((s) => (
                        <MenuItem key={s} value={s}>
                          {s}
                        </MenuItem>
                      ))}
                    </Select>
                  </TableCell>
                    
                  <TableCell>
                    <Select
                      value={newRow.SubjectCode ?? ""}
                      onChange={(e) => handleNewRowChange("SubjectCode", e.target.value)}
                      fullWidth
                      size="small"
                      sx={inputSx}
                      displayEmpty
                      renderValue={(val) => (val ? subjectLabel(val) : "Select")}
                    >
                      <MenuItem value="">
                        <em>Select</em>
                      </MenuItem>
                    
                      {(addRowOptions.Subjects || []).map((s) => {
                        const subjectCode =
                          typeof s === "object" ? s.SubjectCode : String(s);
                      
                        const subjectName =
                          typeof s === "object"
                            ? s.SubjectName || s.SubjectNameCode || ""
                            : "";
                      
                        return (
                          <MenuItem key={subjectCode} value={subjectCode}>
                            {subjectName ? `${subjectCode} - ${subjectName}` : subjectCode}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </TableCell>
                    
                  <TableCell>
                    <Checkbox
                      checked={!!newRow.IsActive}
                      onChange={handleNewRowActiveToggle}
                      sx={checkboxSx}
                    />
                  </TableCell>
                    
                  <TableCell>
                    <IconButton sx={iconBtnSx} onClick={handleAddRow} title="Add">
                      <AddCircleIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>

                {/* no match */}
                {filtersApplied && hasAnyFilterSelected && filteredRows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={10} sx={{ py: 3 }}>
                      <Alert
                        severity="warning"
                        sx={{
                          borderRadius: 2,
                          border: "1px solid rgba(255,152,0,0.25)",
                          backgroundColor: "rgba(255,152,0,0.05)",
                        }}
                      >
                        No results match the selected filters.
                      </Alert>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          </Box>
          </Paper>
     {/*   </Box>
        */}
      </>
    )}

    {tab === 1 && (
      <Paper variant="outlined" sx={sectionSx}>
        <Box sx={{ pl: 1 }}>
          <Typography sx={{ fontWeight: 900, color: THEME_ORANGE }}>
            Bulk Create Degree–Subject Mapping
          </Typography>

          <Typography sx={{ fontSize: "0.85rem", color: "text.secondary", mt: 0.5 }}>
            Select multiple SubCampus, Degree, Branch, Regulation, Semester, and Subjects.
            Existing duplicate mappings will be skipped.
          </Typography>

          <Divider sx={{ my: 2 }} />

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(7, 1fr)" },
              gap: 1.5,
            }}
          >
            {[
              ["SubCampusCode", "SubCampus", bulkOptions.SubCampusCode],
              ["DegreeCode", "Degree", bulkOptions.DegreeCode],
              ["BranchCode", "Branch", bulkOptions.BranchCode],
              ["Regulation", "Regulation", bulkOptions.Regulation],
              ["Semester", "Semester", bulkOptions.Semester],
            ].map(([field, label, list]) => {
              const opts = withSelectAll(list || []);
              const selected = opts.filter((x) =>
                bulkForm[field]?.includes(getOptionValue(x))
              );

              return (
                <Autocomplete
                  key={field}
                  multiple
                  options={opts}
                  value={selected}
                  disableCloseOnSelect
                  getOptionLabel={getOptionLabelText}
                  isOptionEqualToValue={(a, b) => getOptionValue(a) === getOptionValue(b)}
                  onChange={handleBulkMultiSelectAll(field, list || [])}
                  renderOption={(props, option, { selected }) => (
                    <li {...props}>
                      <Checkbox
                        checked={
                          option.isSelectAll
                            ? bulkForm[field]?.length === list.length && list.length > 0
                            : selected
                        }
                        sx={checkboxSx}
                      />
                      {getOptionLabelText(option)}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={`${label}(s)`}
                      size="small"
                      sx={inputSx}
                      helperText={!list.length ? "Select previous dropdown first." : ""}
                    />
                  )}
                />
              );
            })}

            <Autocomplete
              multiple
              options={[
                { SubjectCode: SELECT_ALL_VALUE, SubjectName: "Select All", isSelectAll: true },
                ...(bulkOptions.Subjects || []),
              ]}
              value={(bulkOptions.Subjects || []).filter((x) =>
                bulkForm.SubjectCode.includes(x.SubjectCode)
              )}
              disableCloseOnSelect
              getOptionLabel={(option) =>
                option.isSelectAll
                  ? "Select All"
                  : `${option.SubjectCode} - ${option.SubjectName || ""}`
              }
              isOptionEqualToValue={(a, b) => a.SubjectCode === b.SubjectCode}
              onChange={(_, value) => {
                const hasAll = value.some((x) => x.SubjectCode === SELECT_ALL_VALUE);

                setBulkForm((p) => ({
                  ...p,
                  SubjectCode: hasAll
                    ? (bulkOptions.Subjects || []).map((x) => x.SubjectCode)
                    : value.map((x) => x.SubjectCode),
                }));
              }}
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <Checkbox
                    checked={
                      option.isSelectAll
                        ? bulkForm.SubjectCode.length === bulkOptions.Subjects.length &&
                          bulkOptions.Subjects.length > 0
                        : selected
                    }
                    sx={checkboxSx}
                  />
                  {option.isSelectAll
                    ? "Select All"
                    : `${option.SubjectCode} - ${option.SubjectName || ""}`}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Subject(s)"
                  size="small"
                  sx={inputSx}
                  helperText={
                    bulkOptions.Subjects.length
                      ? "Subjects are loaded from active subject details."
                      : "Select Regulation and Semester to load subjects."
                  }
                />
              )}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={!!bulkForm.IsActive}
                  onChange={(e) =>
                    setBulkForm((p) => ({
                      ...p,
                      IsActive: e.target.checked ? 1 : 0,
                    }))
                  }
                />
              }
              label="Create as Active"
            />
          </Box>

          <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
            Example: 2 branches × 5 subjects = 10 mappings. Existing mappings are skipped.
          </Alert>

          <Button
            variant="contained"
            sx={{ ...primaryBtnSx, mt: 2 }}
            startIcon={bulkCreating ? <CircularProgress size={18} color="inherit" /> : <AddCircleIcon />}
            disabled={bulkCreating}
            onClick={handleBulkCreateMappings}
          >
            {bulkCreating ? "Creating..." : "Bulk Create Mapping"}
          </Button>
        </Box>
      </Paper>
    )}
  </Box> 
</Paper>

      </Paper>
    </Box>
  );
}
